import { useState } from "react";
import { isMobile } from "react-device-detect";
import styled from "styled-components";
import { ProSubscriptionType } from "@lifeat/shared/stripe/StripeSubscriptionTypes";
import { Routes } from "@lifeat/shared/system/RouteConstants";
import { hasFreeAppAccess } from "@lifeat/shared/user/UserUtil";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { LifeAtBodySecondary } from "@common/LifeAtText/LifeAtText";
import LifeAtToggle from "@common/LifeAtToggle/LifeAtToggle";
import { useUserContext } from "@contexts/UserContext";
import { auth } from "@firebase-local/firebase";
import Arrow from "@icons/Arrow-Left-Icon.svg";
import { redirectTo } from "@util/BrowserUtil";
import { buildRoute } from "@util/RouteUtil";
import {
    PricingPageHeader,
    PricingTiersContainer,
    PricingToggleContainer,
} from "../../../features/upgrade-flow/components/PricingPageContent";
import { FreeTier, ProTier } from "../../../features/upgrade-flow/components/PricingTiers";
import { isUserFromAd } from "../OnboardingUtil";
import { SplitLayout } from "../SplitLayout";
import { OnboardingImagePanel } from "./OnboardingImagePanel";

const UpgradeCtaContainer = styled.div`
    width: 100%;
    height: 100%;
    padding: 64px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-image: linear-gradient(#f4e8f9, #a1d0f1);
`;
const UpgradeCtaContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: fit-content;
    gap: 15px;
`;
const ContinueButtonContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-end;
`;
const ArrowIcon = styled(Arrow)`
    fill: ${(props) => props.theme.textColorPrimary};
    transform: rotate(180deg);
`;

const PRICING_TOGGLE_LABEL_MONTHLY = "Monthly";
const PRICING_TOGGLE_LABEL_YEARLY = "Yearly (Save 15%)";

const LIMITED_TIME_OFFER_PAGE_HEADER = "Limited time offer";
const LOGOUT_BUTTON_LABEL = "Log out";
const SKIP_OFFER_CONTINUER_BUTTON_LABEL = "Skip one-time offer";

type OnboardingUpgradeWithTrialProps = {
    onNext: () => void;
};

export function OnboardingUpgradeWithTrial(props: OnboardingUpgradeWithTrialProps) {
    const { userData } = useUserContext();
    const [subscriptionPeriod, setSubscriptionPeriod] = useState<ProSubscriptionType>("annual");
    const isAppBlocked = isUserFromAd() || !hasFreeAppAccess(userData);

    const handlePeriodToggle = () => {
        if (subscriptionPeriod === "monthly") {
            setSubscriptionPeriod("annual");
        } else if (subscriptionPeriod === "annual") {
            setSubscriptionPeriod("monthly");
        }
    };

    const handleLogOut = async () => {
        await auth.signOut();
        redirectTo(buildRoute(Routes.DesktopMain));
    };

    return (
        <SplitLayout
            className="notranslate"
            translate="no"
            left={
                <UpgradeCtaContainer>
                    <UpgradeCtaContent>
                        <PricingPageHeader>{LIMITED_TIME_OFFER_PAGE_HEADER}</PricingPageHeader>
                        <PricingToggleContainer>
                            <LifeAtBodySecondary>{PRICING_TOGGLE_LABEL_MONTHLY}</LifeAtBodySecondary>
                            <LifeAtToggle isEnabled={subscriptionPeriod === "annual"} onChange={handlePeriodToggle} />
                            <LifeAtBodySecondary>{PRICING_TOGGLE_LABEL_YEARLY}</LifeAtBodySecondary>
                        </PricingToggleContainer>
                        <PricingTiersContainer>
                            {!isMobile && !isAppBlocked && <FreeTier />}
                            <ProTier
                                subscriptionPeriod={subscriptionPeriod}
                                amplitudeEventSource={"onboarding"}
                                overrideButtonText={"Get started"}
                            />
                        </PricingTiersContainer>
                        <ContinueButtonContainer>
                            {isAppBlocked && (
                                <LifeAtButtonV2 onClick={handleLogOut} variant="ghost">
                                    {LOGOUT_BUTTON_LABEL}
                                </LifeAtButtonV2>
                            )}
                            {!isAppBlocked && (
                                <LifeAtButtonV2 onClick={props.onNext} variant={"ghost"}>
                                    {SKIP_OFFER_CONTINUER_BUTTON_LABEL}
                                    <ArrowIcon />
                                </LifeAtButtonV2>
                            )}
                        </ContinueButtonContainer>
                    </UpgradeCtaContent>
                </UpgradeCtaContainer>
            }
            right={
                <OnboardingImagePanel src="https://firebasestorage.googleapis.com/v0/b/lifeatspaces-prod.appspot.com/o/images%2Fonboarding%2Ftestimonial.png?alt=media&token=91364323-bc8e-4aea-a625-b9f800d91db2" />
            }
        />
    );
}
